import IconButton from '@/components/Buttons/IconButton';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { contactToUsEmail, pricingPageUrl } from '@/environment';
import CloseWithCircle from '@/images/CloseWithCircle.svg';
import CrossWithDots from '@/images/CrossWithDots.svg';
import Lite from '@/images/Lite.svg';
import PanSquare from '@/images/PanSquare.svg';
import { useAnalytics } from '@/services/analytics';
import theme from '@/theme';
import styled from '@emotion/styled';
import { Button, Dialog } from '@mui/material';
import { useEffect } from 'react';

const StyledDialog = styled(Dialog)`
    .MuiDialog-paper {
        border-radius: 12px;
        display: flex;
        flex-direction: column;
        width: 452px;
        padding: 40px;
        font-family: 'Manrope', sans-serif;
        color: ${theme.colors.black[800]};
    }
`;

const Text = styled.h2`
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: 24px;
    letter-spacing: -0.48px;
    color: ${theme.colors.secondary.main};
    margin-top: 16px;
`;

const Title = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
`;

const CloseButton = styled(IconButton)`
    color: ${theme.colors.black[800]};
    padding: 10px;
    position: absolute;
    right: 4px;
    top: 4px;

    svg {
        width: 18px;
        height: 18px;
    }
`;

const MainText = styled.div`
    color: ${theme.colors.secondary[600]};
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px;
    letter-spacing: -0.28px;
    text-align: center;
    margin-top: 8px;
`;

const StyledIcon = styled.img``;

const StyledButton = styled(Button)`
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    width: 232px;
    height: 44px;
    text-transform: none;
    border-radius: 8px;
    padding: 10px 16px;
    color: ${theme.colors.secondary.main};
    margin-top: 24px;
    box-shadow: none;

    &:hover {
        box-shadow: none;
        background: ${theme.colors.primary.hover};
    }
`;

interface Props {
    isOpen?: boolean;
    isMoreAgents?: boolean;
    onClose: () => void;
}

const CONTENT_CONTACT = {
    title: 'Ready to embed more agents?',
    body: "Let's talk! we're here to help you. Contact us to explore your options",
    button: 'Talk to us',
    icon: PanSquare,
    from: 'embed more agents',
    ctaUrl: contactToUsEmail,
};

const CONTENT_UPGRADE = {
    title: 'Need to embed agents?',
    body: 'Please upgrade your plan to embed agents on your website',
    button: 'Upgrade me',
    icon: Lite,
    from: 'embed agents',
    ctaUrl: pricingPageUrl,
};

export const EmbedAgentModal = ({ isOpen = false, onClose, isMoreAgents = false }: Props) => {
    const mixpanel = useAnalytics();
    const content = isMoreAgents ? CONTENT_CONTACT : CONTENT_UPGRADE;

    useEffect(() => {
        if (isOpen) {
            mixpanel?.track('missing-features', { event: 'click', from: content.from, ctaUrl: content.ctaUrl });
        }
    }, [isOpen]);

    const onSubmit = async () => {
        if (isMoreAgents) {
            window.location.href = `mailto:${contactToUsEmail}`;
        } else {
            window.open(`${pricingPageUrl}/?utm_source=${content.from}`, '_blank');
        }
        onClose();
    };

    return (
        <StyledDialog open={isOpen} onClose={onClose}>
            <Title>
                <StyledIcon src={CrossWithDots} />
                <Text>{content.title}</Text>
            </Title>
            <CloseButton ariaLabel="close" onClick={onClose} src={CloseWithCircle} />
            <MainText>{content.body}</MainText>
            <Title>
                <StyledButton
                    onClick={onSubmit}
                    variant="contained"
                    color="primary"
                    startIcon={<MaskedIcon src={content.icon} size="20px" />}>
                    {content.button}
                </StyledButton>
            </Title>
        </StyledDialog>
    );
};
